// maths.portll.net — "little wins" rubric ticks.
//
// A soft, controlled checkbox list built from a task's `rubric` ([{id,label}]).
// There is NO score and NO right answer — a parent ticks what they happened to
// notice, and that's it. `value` is a controlled array of ticked ids; every change
// re-emits the array in the rubric's own order (so the stored record is stable).
// iPad-safe: the whole row is a ≥44px tap target and the native checkbox stays the
// real focusable control (visually hidden) with the tick box mirroring its state.

import { injectStyles } from "./ui.jsx";

export default function RubricTicks({ rubric, value = [], onChange }) {
  const items = Array.isArray(rubric) ? rubric.filter((r) => r && r.id) : [];
  if (!items.length) return null;

  const ticked = new Set(Array.isArray(value) ? value : []);
  const toggle = (id) => {
    const next = new Set(ticked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    // Re-emit in rubric order for a deterministic stored record.
    onChange && onChange(items.filter((r) => next.has(r.id)).map((r) => r.id));
  };

  return (
    <fieldset className="rt-root">
      <legend className="rt-legend">Tick what you noticed — no wrong answers here</legend>
      <ul className="rt-list">
        {items.map((r) => {
          const checked = ticked.has(r.id);
          return (
            <li key={r.id}>
              <label className={`rt-item${checked ? " rt-item--on" : ""}`}>
                <input
                  type="checkbox"
                  className="rt-input"
                  checked={checked}
                  onChange={() => toggle(r.id)}
                />
                <span className="rt-box" aria-hidden="true">
                  {checked ? "✓" : ""}
                </span>
                <span className="rt-label">{r.label}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}

const RT_CSS = `
.rt-root{border:0;margin:0;padding:0;min-width:0;}
.rt-legend{padding:0;margin:0 0 .6rem;color:var(--ink-soft);font-size:.92rem;}
.rt-list{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:.5rem;}
.rt-item{display:flex;align-items:center;gap:.7rem;min-height:52px;padding:.55rem .8rem;
  background:var(--card);border:1px solid var(--line);border-radius:12px;cursor:pointer;
  transition:border-color .1s ease,background .1s ease;}
.rt-item--on{border-color:color-mix(in srgb,var(--brand) 45%,#fff);
  background:color-mix(in srgb,var(--brand) 7%,#fff);}
.rt-item:active{transform:translateY(1px);}
.rt-input{position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;}
.rt-box{flex:0 0 auto;display:inline-grid;place-items:center;width:28px;height:28px;border-radius:8px;
  border:2px solid color-mix(in srgb,var(--brand) 40%,#fff);background:#fff;color:#fff;font-weight:800;
  font-size:1rem;line-height:1;}
.rt-item--on .rt-box{background:var(--brand);border-color:var(--brand);}
.rt-item:focus-within .rt-box{outline:2px solid var(--sky);outline-offset:2px;}
.rt-label{color:var(--ink);font-weight:600;line-height:1.25;}
`;

injectStyles("maths-rubricticks-styles", RT_CSS);
